import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  styled,
  Box,
  Icon,
} from "@mui/material";
import { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { getObjectDepth } from "src/utils/Utils";
import React from "react";
import { ExplainerTooltip } from "./ExplainerTooltip.js";

// Styled components for "flat" variant
const StyledTableRowHeadFlatSubtitled = styled(TableRow)(({ theme }) => ({
  "& td, & th": {
    border: 0,
    padding: theme.spacing(1, 1),
  },
}));

const StyledTableRowFlatSubtitled = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "& td, & th": {
    border: 0,
    padding: theme.spacing(1.5, 1),
  },
}));

const StyledTableRowHeadFlatSimpled = styled(TableRow)(({ theme }) => ({
  backgroundColor: theme.palette.grey["300"],
  "& td, & th": {
    border: 0,
    padding: "10px",
  },
}));

const StyledTableRowFlatSimpled = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.action.hover,
  },
  "& td, & th": {
    border: 0,
    padding: "10px",
  },
}));

// Styled components for "shadow" variant
const StyledTableContainerShadow = styled(TableContainer)(({ theme }) => ({
  borderRadius: "20px",
  overflow: "hidden",
  boxShadow: theme.shadows.cardLg(theme.palette, theme.palette.mode),
  border: "none",
  backgroundColor: theme.palette.background.paper,
  width: "100%",
  maxWidth: "100%",
}));

const StyledTableRowHeadShadow = styled(TableRow)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark"
      ? theme.palette.overlay.white08
      : theme.palette.tableHeaderBg,
  "& td, & th": {
    border: 0,
    padding: theme.spacing(2, 2),
    borderBottom: "none",
    fontWeight: 700,
  },
}));

const StyledTableRowShadow = styled(TableRow)(({ theme }) => ({
  "& td, & th": {
    border: 0,
    padding: theme.spacing(1.5, 1.5),
    borderBottom: `1px solid ${
      theme.palette.mode === "dark"
        ? theme.palette.overlay.white05
        : theme.palette.overlay.black03
    }`,
  },
  "&:last-child td, &:last-child th": {
    borderBottom: 0,
  },
}));

// Styled components for "dark" variant
const StyledTableContainerDark = styled(TableContainer)(({ theme }) => ({
  borderRadius: "20px",
  overflow: "hidden",
  border: `1px solid rgba(255, 255, 255, 0.06)`,
  backgroundColor: "rgba(255, 255, 255, 0.02)",
  width: "100%",
  maxWidth: "100%",
}));

const StyledTableRowHeadDark = styled(TableRow)(({ theme }) => ({
  backgroundColor: "rgba(255, 255, 255, 0.04)",
  "& td, & th": {
    border: 0,
    padding: theme.spacing(2, 2),
    borderBottom: "none",
    fontWeight: 700,
  },
}));

const StyledTableRowDark = styled(TableRow)(({ theme }) => ({
  "& td, & th": {
    border: 0,
    padding: theme.spacing(1.5, 1.5),
    borderBottom: `1px solid rgba(255, 255, 255, 0.05)`,
  },
  "&:last-child td, &:last-child th": {
    borderBottom: 0,
  },
}));

const ClickableText = styled(Typography)(({ theme, hasExplainer }) => ({
  position: "relative",
  display: "inline-block",
  ...(hasExplainer && {
    textDecorationLine: "underline",
    textDecorationStyle: "dotted",
    textDecorationColor: theme.palette.text.secondary,
    textDecorationThickness: "1px",
    textUnderlineOffset: "2px",
    cursor: "help",
  }),
}));

const FeatureContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(0.5),
}));

const FeatureCell = ({
  title,
  subtitle,
  variant,
  hasSubtitle,
  explainer,
  icon,
  to,
}) => {
  if (variant === "shadow" || variant === "dark") {
    const label = (
      <ClickableText
        variant="body2"
        hasExplainer={Boolean(explainer)}
        {...(to
          ? {
              component: RouterLink,
              to,
              sx: {
                color: variant === "dark" ? "rgba(255, 255, 255, 0.85)" : "text.primary",
                fontWeight: 500,
                fontSize: "0.9rem",
                textDecoration: "underline",
                textUnderlineOffset: "2px",
                cursor: "pointer",
              },
            }
          : {
              sx: {
                color: variant === "dark" ? "rgba(255, 255, 255, 0.85)" : "text.primary",
                fontWeight: 500,
                fontSize: "0.9rem",
              },
            })}
      >
        {subtitle || title}
      </ClickableText>
    );
    return (
      <ExplainerTooltip explainer={explainer} title={subtitle || title}>
        <FeatureContainer>
          {icon && (
            <Icon
              baseClassName="material-symbols-outlined"
              sx={{
                fontSize: "1.1rem",
                color: variant === "dark" ? "rgba(255, 255, 255, 0.5)" : "text.secondary",
                fontWeight: 300,
                marginTop: "2px",
              }}
            >
              {icon}
            </Icon>
          )}
          {label}
        </FeatureContainer>
      </ExplainerTooltip>
    );
  }
  return <Typography variant="subtitle2">{subtitle || title}</Typography>;
};

/**
 * PlanComparisonTable Component
 * @param {Object} sx - Custom styles for the table container.
 * @param {Object} data - Data object containing columns and body for the table.
 * @param {string} [variant="shadow"] - Variant of the table, either "flat" or "shadow".
 */
export const PlanComparisonTable = ({ sx, data, variant = "shadow", previewRows = null, onExpand = null }) => {
  const [expanded, setExpanded] = useState(false);
  const expand = () => {
    setExpanded(true);
    onExpand?.();
  };
  const hasSubtitle = useMemo(
    () => getObjectDepth(data.body) === 3,
    [data.body]
  );

  const bodyKeys = useMemo(() => Object.keys(data.body), [data.body]);
  const collapsed = previewRows != null && !expanded && previewRows < bodyKeys.length;
  const visibleKeys = collapsed ? bodyKeys.slice(0, previewRows) : bodyKeys;

  const columnsWithTargets = useMemo(
    () => data.columns.filter((e) => Boolean(e.target)),
    [data.columns]
  );

  const TableContainerComponent =
    variant === "shadow"
      ? StyledTableContainerShadow
      : variant === "dark"
      ? StyledTableContainerDark
      : TableContainer;
  const TableRowHeadComponent =
    variant === "shadow"
      ? StyledTableRowHeadShadow
      : variant === "dark"
      ? StyledTableRowHeadDark
      : hasSubtitle
      ? StyledTableRowHeadFlatSubtitled
      : StyledTableRowHeadFlatSimpled;
  const TableRowBodyComponent =
    variant === "shadow"
      ? StyledTableRowShadow
      : variant === "dark"
      ? StyledTableRowDark
      : hasSubtitle
      ? StyledTableRowFlatSubtitled
      : StyledTableRowFlatSimpled;

  // Function to render cell content based on variant
  const renderCellContent = (content, isLastColumn = false) => {
    if (variant === "shadow") {
      return (
        <Typography
          variant="body2"
          fontWeight={isLastColumn ? 700 : 500}
          sx={{
            color: isLastColumn ? "primary.main" : "text.primary",
            fontSize: "0.8rem",
          }}
        >
          {content}
        </Typography>
      );
    }
    if (variant === "dark") {
      return (
        <Typography
          variant="body2"
          fontWeight={isLastColumn ? 700 : 500}
          sx={{
            color: isLastColumn ? "rgba(255, 255, 255, 0.9)" : "rgba(255, 255, 255, 0.75)",
            fontSize: "0.8rem",
          }}
        >
          {content}
        </Typography>
      );
    }
    return (
      <Typography
        variant="subtitle2"
        fontWeight={isLastColumn ? 600 : "normal"}
      >
        {content}
      </Typography>
    );
  };

  // Table content
  const tableContent = (
    <Table>
      <TableHead>
        <TableRowHeadComponent>
          {data.columns.map((column, index) => (
            <TableCell
              key={index}
              align={column.align ?? "left"}
              scope={column.scope ?? "auto"}
            >
              <Typography
                variant={
                  variant === "shadow" || variant === "dark" ? "h6" : hasSubtitle ? "h5" : "subtitle2"
                }
                fontWeight={variant === "shadow" || variant === "dark" ? 700 : 600}
                sx={
                  variant === "shadow"
                    ? {
                        fontSize: "1.1rem",
                        color: "text.primary",
                        letterSpacing: "-0.01em",
                      }
                    : variant === "dark"
                    ? {
                        fontSize: "1.1rem",
                        color: "rgba(255, 255, 255, 0.95)",
                        letterSpacing: "-0.01em",
                      }
                    : undefined
                }
              >
                {column.label}
              </Typography>
              {column.subtitle && (variant === "shadow" || variant === "dark") && (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.875rem",
                    color: variant === "dark" ? "rgba(255, 255, 255, 0.5)" : "text.secondary",
                    fontWeight: 400,
                    mt: 0.5,
                  }}
                >
                  {column.subtitle}
                </Typography>
              )}
            </TableCell>
          ))}
        </TableRowHeadComponent>
      </TableHead>
      <TableBody>
        {visibleKeys.map((title, titleIndex) => (
          <React.Fragment key={title}>
            {hasSubtitle ? (
              <>
                {/* Render title row for "flat" variant with subtitles */}
                {variant === "flat" && (
                  <TableRowBodyComponent>
                    <TableCell colSpan={data.columns.length}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        letterSpacing={"0.4px"}
                        sx={{ color: "text.primary" }}
                      >
                        {title}
                      </Typography>
                    </TableCell>
                  </TableRowBodyComponent>
                )}
                {/* Render subtitle rows */}
                {Object.keys(data.body[title]).map((subtitle) => {
                  const explainer =
                    variant === "shadow" || variant === "dark"
                      ? data.body[title]?.[subtitle]?.explainer ||
                        data.explainers?.[title]?.[subtitle]
                      : null;
                  const icon =
                    variant === "shadow" || variant === "dark"
                      ? data.body[title]?.[subtitle]?.icon
                      : null;
                  return (
                    <TableRowBodyComponent key={`${title}-${subtitle}`}>
                      <TableCell>
                        <FeatureCell
                          title={title}
                          subtitle={subtitle}
                          variant={variant}
                          hasSubtitle={hasSubtitle}
                          explainer={explainer}
                          icon={icon}
                        />
                      </TableCell>
                      {columnsWithTargets.map((column, columnIndex) => (
                        <TableCell key={columnIndex} align="center">
                          {renderCellContent(
                            data.body[title][subtitle][column.target],
                            columnIndex === columnsWithTargets.length - 1
                          )}
                        </TableCell>
                      ))}
                    </TableRowBodyComponent>
                  );
                })}
                {/* Add empty row between title sections for "flat" variant */}
                {variant === "flat" &&
                  titleIndex < visibleKeys.length - 1 && (
                    <TableRowBodyComponent>
                      <TableCell colSpan={data.columns.length}>
                        &nbsp;
                      </TableCell>
                    </TableRowBodyComponent>
                  )}
              </>
            ) : (
              /* Render rows directly for no subtitles */
              <TableRowBodyComponent>
                <TableCell>
                  {(() => {
                    const explainer =
                      variant === "shadow" || variant === "dark"
                        ? data.body[title]?.explainer ||
                          data.explainers?.[title]
                        : null;
                    const icon =
                      variant === "shadow" || variant === "dark" ? data.body[title]?.icon : null;
                    return (
                      <FeatureCell
                        title={title}
                        variant={variant}
                        hasSubtitle={hasSubtitle}
                        explainer={explainer}
                        icon={icon}
                        to={data.body[title]?.to}
                      />
                    );
                  })()}
                </TableCell>
                {columnsWithTargets.map((column, index) => (
                  <TableCell key={index} align="center">
                    {renderCellContent(
                      data.body[title][column.target],
                      index === columnsWithTargets.length - 1
                    )}
                  </TableCell>
                ))}
              </TableRowBodyComponent>
            )}
          </React.Fragment>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <Box sx={sx}>
      <Box sx={{ position: "relative" }}>
        <TableContainerComponent>{tableContent}</TableContainerComponent>
        {collapsed && (
          <>
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                height: 96,
                pointerEvents: "none",
                background: "linear-gradient(to bottom, rgba(255,255,255,0), #fff)",
              }}
            />
            <Box sx={{ position: "absolute", left: 0, right: 0, bottom: 12, display: "flex", justifyContent: "center" }}>
              <Box
                component="button"
                type="button"
                onClick={expand}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  backgroundColor: "#fff",
                  border: "1px solid",
                  borderColor: "overlay.black10",
                  borderRadius: "999px",
                  px: 2,
                  py: 1,
                  cursor: "pointer",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  color: "text.primary",
                  fontWeight: 800,
                  fontSize: "0.72rem",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  fontFamily: "inherit",
                }}
              >
                See all
                <Icon baseClassName="material-symbols-outlined" sx={{ fontSize: "1.05rem" }}>
                  expand_more
                </Icon>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};
